import type { Locale } from '@warehouse/shared';

/**
 * Formatting is Swedish-first by design: Gigachop's warehouse floor, its
 * printouts and its labels are Swedish (ARCHITECTURE §9), and a date rendered
 * `2026-07-27` means the same thing in every language we ship. So `sv-SE`
 * (ISO-shaped dates, space thousands separator, comma decimal) is the DEFAULT
 * for every locale unless the user's UI language genuinely changes the answer.
 *
 * Quantities arrive as STRINGS (numeric(14,3) — see `qtySchema`): parsing them
 * into a JS float before display would reintroduce exactly the precision loss
 * the string transport exists to prevent, so the integer/decimal split is done
 * textually.
 */

const INTL_LOCALE: Record<Locale, string> = {
  sv: 'sv-SE',
  en: 'sv-SE', // ISO dates + metric grouping stay identical; only UI copy differs.
  zh: 'sv-SE',
};

export function intlLocale(locale: Locale = 'sv'): string {
  return INTL_LOCALE[locale] ?? 'sv-SE';
}

function toDate(value: Date | string | number | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** `2026-07-27` */
export function formatDate(value: Date | string | null | undefined, locale?: Locale): string {
  const date = toDate(value);
  if (!date) return '—';
  return new Intl.DateTimeFormat(intlLocale(locale), {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'Europe/Stockholm',
  }).format(date);
}

/** `2026-07-27 14:05` — Europe/Stockholm, the warehouse's wall clock. */
export function formatDateTime(value: Date | string | null | undefined, locale?: Locale): string {
  const date = toDate(value);
  if (!date) return '—';
  return new Intl.DateTimeFormat(intlLocale(locale), {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Europe/Stockholm',
  }).format(date);
}

/** `14:05` */
export function formatTime(value: Date | string | null | undefined, locale?: Locale): string {
  const date = toDate(value);
  if (!date) return '—';
  return new Intl.DateTimeFormat(intlLocale(locale), {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Europe/Stockholm',
  }).format(date);
}

/**
 * Relative age, for "last seen" columns on the device fleet and the scanner
 * staleness banner. Falls back to an absolute timestamp beyond a week.
 */
export function formatRelative(value: Date | string | null | undefined, locale?: Locale): string {
  const date = toDate(value);
  if (!date) return '—';
  const diffMs = date.getTime() - Date.now();
  const absMinutes = Math.abs(diffMs) / 60_000;
  if (absMinutes > 60 * 24 * 7) return formatDateTime(date, locale);

  const rtf = new Intl.RelativeTimeFormat(intlLocale(locale), { numeric: 'auto' });
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['minute', 60_000],
    ['hour', 3_600_000],
    ['day', 86_400_000],
  ];
  let chosen: [Intl.RelativeTimeFormatUnit, number] = ['minute', 60_000];
  for (const unit of units) {
    if (Math.abs(diffMs) >= unit[1]) chosen = unit;
  }
  return rtf.format(Math.round(diffMs / chosen[1]), chosen[0]);
}

/**
 * Quantity display. Trailing zeros are trimmed (`12.500` → `12,5`) because a
 * pick list reads better without them, but significant decimals are never
 * rounded away. Pass `decimals` to force a fixed width (comparison columns).
 */
export function formatQty(
  value: string | number | null | undefined,
  options: { decimals?: number; locale?: Locale; unit?: string } = {},
): string {
  if (value === null || value === undefined || value === '') return '—';
  const raw = typeof value === 'number' ? value.toFixed(options.decimals ?? 3) : value.trim();
  if (!/^-?\d+(\.\d+)?$/.test(raw)) return String(value);

  const negative = raw.startsWith('-');
  const [intPartRaw = '0', fracRaw = ''] = raw.replace('-', '').split('.');
  let frac = fracRaw;
  if (options.decimals === undefined) {
    frac = frac.replace(/0+$/, '');
  } else {
    frac = frac.padEnd(options.decimals, '0').slice(0, options.decimals);
  }

  const grouped = groupThousands(intPartRaw);
  const body = frac.length > 0 ? `${grouped},${frac}` : grouped;
  const text = negative ? `−${body}` : body;
  return options.unit ? `${text} ${options.unit}` : text;
}

/** Non-breaking-space thousands grouping — the sv-SE convention. */
function groupThousands(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

export function formatInteger(value: number | null | undefined, locale?: Locale): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat(intlLocale(locale), { maximumFractionDigits: 0 }).format(value);
}

/** `<input type="date">` value in the warehouse's timezone, not the browser's. */
export function toDateInputValue(value: Date | string | null | undefined): string {
  const date = toDate(value);
  if (!date) return '';
  return new Intl.DateTimeFormat('sv-SE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'Europe/Stockholm',
  }).format(date);
}

export function fullName(user: { firstName?: string | null; lastName?: string | null } | null | undefined): string {
  if (!user) return '';
  return [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
}

export function initials(user: { firstName?: string | null; lastName?: string | null; email?: string } | null): string {
  if (!user) return '?';
  const first = user.firstName?.trim()?.[0];
  const last = user.lastName?.trim()?.[0];
  if (first || last) return `${first ?? ''}${last ?? ''}`.toUpperCase();
  return (user.email?.[0] ?? '?').toUpperCase();
}
